import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import api from '../../services/api';

const HomeworkManager = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', lessonId: '' });

  useEffect(() => {
    fetchHomeworks();
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const res = await api.get('/lessons');
      if (res.data.success) {
        setLessons(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load lessons', err);
    }
  };

  const fetchHomeworks = async () => {
    try {
      setLoading(true);
      const res = await api.get('/homework');
      if (res.data.success) {
        setHomeworks(res.data.data);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to load homework');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/homework', formData);
      if (res.data.success) {
        setHomeworks([res.data.data, ...homeworks]);
        setShowModal(false);
        setFormData({ title: '', description: '', lessonId: '' });
        alert('Homework assigned successfully');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to assign homework');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this homework? All related submissions will also be deleted.')) return;
    try {
      await api.delete(`/homework/${id}`);
      setHomeworks(homeworks.filter(h => h._id !== id));
      alert('Homework deleted');
    } catch (err) {
      console.error(err);
      alert('Failed to delete homework');
    }
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Homework Assignments</h2>
          <p className="text-gray-600 text-sm mt-1">Assign text-based homework tasks to students</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
        >
          <FaPlus className="mr-2" /> Assign Homework
        </button>
      </div>

      <div className="p-6">
        {homeworks.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No homework assignments found.</p>
        ) : (
          <div className="space-y-4">
            {homeworks.map(hw => (
              <div key={hw._id} className="p-4 border border-gray-200 rounded-lg flex justify-between items-start hover:shadow-sm">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{hw.title}</h3>
                  <p className="text-gray-700 mt-2 whitespace-pre-wrap">{hw.description}</p>
                  <div className="flex text-xs text-gray-500 mt-4 space-x-4">
                    <span>Assigned By: {hw.assignedBy?.name || 'Admin'}</span>
                    <span>Date: {new Date(hw.createdAt).toLocaleDateString()}</span>
                    {hw.lessonId && <span>Lesson: {hw.lessonId.lessonNumber} - {hw.lessonId.title}</span>}
                  </div>
                </div>
                <button onClick={() => handleDelete(hw._id)} className="text-red-500 hover:text-red-700 p-2">
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Assign New Homework</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description / Instructions</label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Associated Lesson</label>
                <select
                  required
                  value={formData.lessonId}
                  onChange={e => setFormData({ ...formData, lessonId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 bg-white"
                >
                  <option value="">Select a lesson</option>
                  {lessons.map(l => (
                    <option key={l._id} value={l._id}>
                      Lesson {l.lessonNumber} - {l.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeworkManager;
